import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Favorite as HeartIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getAllSurvivorshipPlans } from '../../services/oncologyService';
import { PageHeader, ContentCard, HeaderButton } from './oncologyStyles';

const SurvivorshipList = () => {
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Search/Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchPlans = useCallback(async () => {
    if (!defaultFacility?.id) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getAllSurvivorshipPlans(defaultFacility.id, {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        status: statusFilter
      });
      setPlans(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Error fetching survivorship plans:', err);
      setError('Failed to load survivorship care plans');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, page, rowsPerPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleSearch = () => {
    setPage(0);
    fetchPlans();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(0);
  };

  const handleViewPlan = (plan) => {
    navigate(`/oncology/survivorship/${plan.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'Draft': 'default',
      'Active': 'success',
      'Completed': 'info',
      'On Hold': 'warning'
    };
    return colorMap[status] || 'default';
  };

  const planStatuses = ['Draft', 'Active', 'Completed', 'On Hold'];

  return (
    <Box>
      <PageHeader
        title="Survivorship Care"
        subtitle="Post-treatment care plans, surveillance schedules, and late effects monitoring"
        icon={<HeartIcon />}
        backTo="/oncology"
        actions={
          <HeaderButton
            startIcon={<AddIcon />}
            onClick={() => navigate('/oncology/survivorship/new')}
          >
            New Plan
          </HeaderButton>
        }
      />

      <ContentCard>
        <CardContent>
          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by patient name or diagnosis..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {planStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>

            {(searchTerm || statusFilter) && (
              <Button size="small" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Results Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : plans.length === 0 ? (
            <Alert severity="info">
              No survivorship care plans found. Use the filters above to search or click "New Plan" to create one.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient</TableCell>
                      <TableCell>Diagnosis</TableCell>
                      <TableCell>Treatment Completed</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Surveillance Items</TableCell>
                      <TableCell align="center">Late Effects</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow 
                        key={plan.id} 
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewPlan(plan)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {plan.patientName || 'Unknown Patient'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {plan.patientMRN || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {plan.treatmentSummary?.diagnosisSummary || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(plan.treatmentSummary?.completionDate)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={plan.status || 'Draft'}
                            size="small"
                            color={getStatusColor(plan.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={plan.surveillanceSchedule?.length || 0}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={plan.potentialLateEffects?.length || 0}
                            size="small"
                            variant="outlined"
                            color={plan.potentialLateEffects?.length > 0 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{formatDate(plan.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPlan(plan);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Print functionality
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </>
          )}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default SurvivorshipList;
